import type { ChangeEventHandler, MouseEventHandler } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Header, IconButton, Input, Scroll, Spinner, Text, config, Chip, toRem } from 'folds';
import type { EventTimelineSet, MatrixEvent, Room, Thread } from '$types/matrix-sdk';
import { NotificationCountType, RoomEvent, ThreadEvent } from '$types/matrix-sdk';
import { useAtomValue } from 'jotai';
import { useMatrixClient } from '$hooks/useMatrixClient';
import { useRoomNavigate } from '$hooks/useRoomNavigate';
import { nicknamesAtom } from '$state/nicknames';
import { profilesCacheAtom } from '$state/userRoomProfile';
import { useRoomMemberHydration } from '$hooks/useRoomMemberHydration';
import { getMemberDisplayName } from '$utils/room/display';
import { reactionOrEditEvent } from '$utils/room/relations';
import { getMxIdLocalPart } from '$utils/matrix';
import { Chats, chipIcon, composerIcon, MagnifyingGlass, X } from '$components/icons/phosphor';
import { settingsAtom } from '$state/settings';
import { useSetting } from '$state/hooks/settings';
import { MessagePreview, useRoomMessagePreviewRenderer } from '$components/message-preview';
import { UnreadBadge } from '$components/unread-badge';
import * as css from './ThreadDrawer.css';
import { SidebarResizer } from '$pages/client/sidebar/SidebarResizer';
import { isMobileOrTablet } from '$utils/platform';
import { useDismissOnBack } from '$utils/androidBack';
import { useMatrixEvent } from '$hooks/useMatrixEvent';

type ThreadPreviewProps = {
  room: Room;
  thread: Thread;
  onClick: (threadId: string) => void;
  onJump?: () => void;
};

function ThreadPreview({ room, thread, onClick, onJump }: ThreadPreviewProps) {
  const { navigateRoom } = useRoomNavigate();
  const nicknames = useAtomValue(nicknamesAtom);
  const cachedProfiles = useAtomValue(profilesCacheAtom);
  const [hour24Clock] = useSetting(settingsAtom, 'hour24Clock');
  const [dateFormatString] = useSetting(settingsAtom, 'dateFormatString');
  const renderContent = useRoomMessagePreviewRenderer(room);

  const handleJumpClick: MouseEventHandler = useCallback(
    (evt) => {
      evt.stopPropagation();
      navigateRoom(room.roomId, thread.id);
      onJump?.();
    },
    [navigateRoom, room.roomId, thread.id, onJump]
  );

  const [, forceUnread] = useState(0);
  const onUnread = useCallback(
    (_count: unknown, threadId?: string) => {
      if (!threadId || threadId === thread.id) forceUnread((n) => n + 1);
    },
    [thread.id]
  );
  useMatrixEvent(room, RoomEvent.UnreadNotifications, onUnread);
  const unreadTotal = room.getThreadUnreadNotificationCount(thread.id, NotificationCountType.Total);
  const unreadHighlight = room.getThreadUnreadNotificationCount(
    thread.id,
    NotificationCountType.Highlight
  );

  const { rootEvent } = thread;
  const lastReply = thread.events.findLast(
    (ev: MatrixEvent) => ev.getId() !== thread.id && !reactionOrEditEvent(ev)
  );
  const lastSenderId = lastReply?.getSender() ?? '';
  useRoomMemberHydration(room, lastSenderId);

  if (!rootEvent) return null;

  const localReplyCount = thread.events.filter(
    (ev: MatrixEvent) => ev.getId() !== thread.id && !reactionOrEditEvent(ev)
  ).length;
  // Use Math.max so we never show fewer replies than the server reports.
  const replyCount = Math.max(localReplyCount, thread.length ?? 0);

  const lastDisplayName =
    getMemberDisplayName(room, lastSenderId, nicknames) ??
    cachedProfiles[lastSenderId]?.displayName ??
    getMxIdLocalPart(lastSenderId) ??
    lastSenderId;
  const lastContent = lastReply?.getContent();
  const lastBody: string = typeof lastContent?.body === 'string' ? lastContent.body : '';

  return (
    <Box
      as="button"
      direction="Column"
      gap="100"
      className={css.ThreadBrowserItem}
      onClick={() => onClick(thread.id)}
    >
      <MessagePreview
        room={room}
        event={rootEvent}
        renderContent={renderContent}
        actions={
          <Box shrink="No" alignItems="Center" gap="200">
            {unreadTotal > 0 && (
              <UnreadBadge unread={{ total: unreadTotal, highlight: unreadHighlight }} />
            )}
            <Chip data-event-id={thread.id} onClick={handleJumpClick} radii="Pill">
              <Text size="T200">Jump</Text>
            </Chip>
          </Box>
        }
        onOpen={handleJumpClick}
        hour24Clock={hour24Clock}
        dateFormatString={dateFormatString}
      />
      {replyCount > 0 && (
        <Box gap="100" alignItems="Center" style={{ marginTop: config.space.S200 }}>
          <Text size="T200" priority="300" style={{ flexShrink: 0 }}>
            {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
          </Text>
          {lastReply && lastBody && (
            <Text
              size="T200"
              priority="300"
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              · {lastDisplayName}: {lastBody.slice(0, 60)}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
}

type ThreadBrowserProps = {
  room: Room;
  onOpenThread: (threadId: string) => void;
  onClose: () => void;
  overlay?: boolean;
};

export function ThreadBrowser({ room, onOpenThread, onClose, overlay }: ThreadBrowserProps) {
  const mx = useMatrixClient();
  const [, forceUpdate] = useState(0);
  const [query, setQuery] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [canLoadMore, setCanLoadMore] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const threadListTimelineSetRef = useRef<EventTimelineSet | null>(null);
  const loadingMoreRef = useRef(false);
  const canLoadMoreRef = useRef(false);
  canLoadMoreRef.current = canLoadMore;
  useDismissOnBack(onClose);

  // On mount, set up thread event listeners, create the server-side thread
  // timeline sets, then fetch page 1 via paginate.  The two operations are
  // sequenced in a single effect so that createThreadsTimelineSets() always
  // resolves before fetchRoomThreads() runs — the SDK's fetchRoomThreadList
  // has an early-return guard (`if (this.threadsTimelineSets.length === 0)`)
  // that silently no-ops when the sets haven't been created yet, so running
  // both in parallel (the old two-effect approach) caused fetchRoomThreads to
  // always be a no-op and left threadsReady=true prematurely.
  useEffect(() => {
    const onUpdate = () => forceUpdate((n) => n + 1);
    room.on(ThreadEvent.New, onUpdate);
    room.on(ThreadEvent.Update, onUpdate);
    room.on(ThreadEvent.NewReply, onUpdate);

    let cancelled = false;
    const loadThreads = async () => {
      setLoadingMore(true);
      try {
        // Create the timeline sets first — required before fetchRoomThreads().
        const sets = await room.createThreadsTimelineSets();
        if (!sets || cancelled) return;
        const [allThreadsSet] = sets;
        threadListTimelineSetRef.current = allThreadsSet;

        // Now fetch page 1 from the /threads endpoint.  threadsTimelineSets is
        // populated so fetchRoomThreadList will not early-return.
        await room.fetchRoomThreads().catch((err: unknown) => {
          console.warn('ThreadBrowser: fetchRoomThreads failed', err);
        });

        // Paginate to load the first page into the timeline set.
        const hasMore = await mx.paginateEventTimeline(allThreadsSet.getLiveTimeline(), {
          backwards: true,
        });
        // Ensure Thread objects exist for server-returned thread roots not yet
        // known locally (threads outside the current sliding-sync window).
        // fetchRoomThreads() creates Thread objects internally but uses
        // room.findEventById() to set rootEvent — if the root event isn't in
        // the sliding-sync cache, rootEvent ends up undefined, and
        // ThreadPreview returns null for those threads.  Backfill here using
        // the event we already have from the threads timeline set.
        allThreadsSet
          .getLiveTimeline()
          .getEvents()
          .filter((event) => !!event.getId())
          .forEach((event) => {
            const id = event.getId()!;
            const existingThread = room.getThread(id);

            if (!existingThread) {
              room.createThread(id, event, [], false);
            } else {
              if (!existingThread.rootEvent) {
                existingThread.rootEvent = event;
                existingThread.setEventMetadata(event);
              }
            }
          });
        if (!cancelled) {
          setCanLoadMore(hasMore);
          forceUpdate((n) => n + 1);
        }
      } catch {
        // Server doesn't support thread list API; fall back to locally known threads.
      } finally {
        if (!cancelled) setLoadingMore(false);
      }
    };
    loadThreads();

    return () => {
      cancelled = true;
      room.off(ThreadEvent.New, onUpdate);
      room.off(ThreadEvent.Update, onUpdate);
      room.off(ThreadEvent.NewReply, onUpdate);
    };
  }, [room, mx]);

  const handleLoadMore = useCallback(async () => {
    const tls = threadListTimelineSetRef.current;
    if (!tls || loadingMore) return;
    setLoadingMore(true);
    try {
      const hasMore = await mx.paginateEventTimeline(tls.getLiveTimeline(), {
        backwards: true,
      });
      tls
        .getLiveTimeline()
        .getEvents()
        .filter((event) => !!event.getId())
        .forEach((event) => {
          const id = event.getId()!;
          const existingThread = room.getThread(id);

          if (!existingThread) {
            room.createThread(id, event, [], false);
          } else {
            if (!existingThread.rootEvent) {
              existingThread.rootEvent = event;
              existingThread.setEventMetadata(event);
            }
          }
        });
      setCanLoadMore(hasMore);
      forceUpdate((n) => n + 1);
    } catch {
      // ignore
    } finally {
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [mx, room, loadingMore]);

  const handleLoadMoreRef = useRef(handleLoadMore);
  handleLoadMoreRef.current = handleLoadMore;

  const handleThreadsScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 200 && canLoadMoreRef.current && !loadingMoreRef.current) {
      handleLoadMoreRef.current();
    }
  }, []);

  const allThreads = room.getThreads().toSorted((a: Thread, b: Thread) => {
    const aTs = a.events.at(-1)?.getTs() ?? a.rootEvent?.getTs() ?? 0;
    const bTs = b.events.at(-1)?.getTs() ?? b.rootEvent?.getTs() ?? 0;
    return bTs - aTs;
  });

  const lowerQuery = query.trim().toLowerCase();
  const threads = lowerQuery
    ? allThreads.filter((t: Thread) => {
        const body = t.rootEvent?.getContent()?.body ?? '';
        return typeof body === 'string' && body.toLowerCase().includes(lowerQuery);
      })
    : allThreads;

  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setQuery(e.target.value);
  };

  const [threadSidebarWidth, setThreadSidebarWidth] = useSetting(
    settingsAtom,
    'threadSidebarWidth'
  );
  const [curWidth, setCurWidth] = useState(threadSidebarWidth);
  useEffect(() => {
    setCurWidth(threadSidebarWidth);
  }, [threadSidebarWidth]);
  return (
    <Box
      className={overlay ? css.ThreadDrawerOverlay : css.ThreadDrawer}
      direction="Column"
      shrink="No"
      style={{
        position: 'relative',
        width: overlay ? '100%' : toRem(curWidth),
      }}
    >
      {!isMobileOrTablet() && (
        <SidebarResizer
          setCurWidth={setCurWidth}
          sidebarWidth={threadSidebarWidth}
          setSidebarWidth={setThreadSidebarWidth}
          minValue={150}
          maxValue={600}
          isReversed
        />
      )}
      <Header className={css.ThreadDrawerHeader} variant="Background" size="600">
        <Box grow="Yes" alignItems="Center" gap="200">
          {composerIcon(Chats)}
          <Text size="H4" truncate>
            Threads
          </Text>
        </Box>
        <Box alignItems="Center" gap="200" shrink="No">
          <IconButton
            onClick={onClose}
            variant="SurfaceVariant"
            size="300"
            radii="300"
            aria-label="Close threads"
          >
            {composerIcon(X)}
          </IconButton>
        </Box>
      </Header>

      <Box
        direction="Column"
        gap="100"
        style={{ padding: `${config.space.S200} ${config.space.S300}` }}
        shrink="No"
      >
        <Input
          ref={searchRef}
          value={query}
          onChange={handleSearchChange}
          placeholder="Search threads..."
          variant="Surface"
          size="400"
          radii="400"
          before={chipIcon(MagnifyingGlass)}
          after={
            query ? (
              <IconButton
                size="300"
                radii="300"
                variant="SurfaceVariant"
                onClick={() => {
                  setQuery('');
                  searchRef.current?.focus();
                }}
                aria-label="Clear search"
              >
                {chipIcon(X)}
              </IconButton>
            ) : undefined
          }
        />
      </Box>

      <Box className={css.ThreadDrawerContent} grow="Yes" direction="Column">
        <Scroll
          variant="Background"
          visibility="Hover"
          direction="Vertical"
          size="300"
          onScroll={handleThreadsScroll}
          style={{ flexGrow: 1 }}
        >
          {(() => {
            if (threads.length === 0 && loadingMore)
              return (
                <Box
                  direction="Column"
                  alignItems="Center"
                  justifyContent="Center"
                  style={{ padding: config.space.S400, gap: config.space.S200 }}
                >
                  <Spinner variant="Secondary" size="400" />
                </Box>
              );
            if (threads.length === 0)
              return (
                <Box
                  direction="Column"
                  alignItems="Center"
                  justifyContent="Center"
                  style={{ padding: config.space.S400, gap: config.space.S200 }}
                >
                  {composerIcon(Chats, { style: { opacity: 0.6 } })}
                  <Text size="T300" align="Center">
                    {lowerQuery ? 'No threads match your search.' : 'No threads yet.'}
                  </Text>
                </Box>
              );
            return (
              <>
                <Box
                  direction="Column"
                  style={{ padding: `${config.space.S100} ${config.space.S200}` }}
                >
                  {threads.map((thread: Thread) => (
                    <ThreadPreview
                      key={thread.id}
                      room={room}
                      thread={thread}
                      onClick={onOpenThread}
                      onJump={onClose}
                    />
                  ))}
                </Box>
                {loadingMore && (
                  <Box
                    justifyContent="Center"
                    style={{ padding: config.space.S300, flexShrink: 0 }}
                  >
                    <Spinner variant="Secondary" size="400" />
                  </Box>
                )}
              </>
            );
          })()}
        </Scroll>
      </Box>
    </Box>
  );
}
